/**
 * Package Type Tabs
 *
 * Ordering and initial selection of the booking engine package type tabs, driven
 * by `guiSettings.customizations.defaultBookingEngineTab`.
 *
 * The config is read from here — a module only components import — and never from
 * the store: `@/utilities` sits in an import cycle with `@/router` → `@/services`
 * → `@running_app_src/store`, so anything the store evaluates at module scope
 * reaches a half-initialized `get_config`. Components mount long after that
 * settles, and after app_config() populated `$config`.
 */

import { get_config } from '@/utilities';
import { PACKAGE_TYPE_PROVIDER } from '@alpitour/resources/package_types';

// Reverse map: config provider name → package type
const provider_package_type = Object.fromEntries(
  Object.entries(PACKAGE_TYPE_PROVIDER).map(([package_type, provider]) => [
    provider,
    package_type,
  ]),
);

/**
 * Package type the backoffice asks the booking engine to open on, or an empty
 * string when the customization is missing or names an unknown provider. Callers
 * read the empty string as "no opinion" and keep their own default.
 */
export function get_configured_package_type() {
  const provider = get_config(
    'guiSettings.customizations.defaultBookingEngineTab',
    '',
  );

  let package_type = '';
  if (provider_package_type[provider]) {
    package_type = provider_package_type[provider];
  }
  return package_type;
}

/**
 * Package type the booking engine opens on.
 *
 * The configured tab applies once per session — `default_tab_set` latches it — so
 * a refresh keeps whichever tab the user ended up with. It is only considered
 * when it is actually allowed, so an unprovisioned flow never consumes the latch.
 * A current package type that is no longer allowed falls back to the first tab.
 */
export function resolve_initial_package_type({
  allowed_package_types,
  configured_package_type,
  current_package_type,
  default_tab_set,
}) {
  let package_type = allowed_package_types[0];

  if (allowed_package_types.indexOf(current_package_type) !== -1) {
    package_type = current_package_type;
  }

  if (
    !default_tab_set &&
    allowed_package_types.indexOf(configured_package_type) !== -1
  ) {
    package_type = configured_package_type;
  }

  return package_type;
}
